import { z } from 'zod';
import type { VersionCatalog } from './versions.js';

// Sources requested for the property migration and rule types/defaults:
// https://minecraft.wiki/w/Server.properties
// https://minecraft.wiki/w/Game_rule
// https://minecraft.wiki/w/Java_Edition_1.21.9
// https://minecraft.wiki/w/Java_Edition_26.1
// Wiki access is blocked in the review environment; cross-checked against Mojang's release notes:
// https://www.minecraft.net/en-us/article/minecraft-java-edition-1-21-9 (all four: boolean, default true)
// https://www.minecraft.net/en-us/article/minecraft-java-edition-1-21-11 (namespaced registry IDs)
// https://www.minecraft.net/en-us/article/minecraft-java-edition-26-1 (shared data/minecraft/game_rules.dat)
export const gameSettingRules = {
  'allow-nether': {
    type: 'boolean',
    defaultValue: true,
    old: 'allowEnteringNetherUsingPortals',
    modern: 'minecraft:allow_entering_nether_using_portals',
  },
  pvp: { type: 'boolean', defaultValue: true, old: 'pvp', modern: 'minecraft:pvp' },
  'spawn-monsters': { type: 'boolean', defaultValue: true, old: 'spawnMonsters', modern: 'minecraft:spawn_monsters' },
  'enable-command-block': {
    type: 'boolean',
    defaultValue: true,
    old: 'commandBlocksEnabled',
    modern: 'minecraft:command_blocks_work',
  },
} as const;
export type RuleSettingKey = keyof typeof gameSettingRules;
export const RuleSettingKeySchema = z.enum(['allow-nether', 'pvp', 'spawn-monsters', 'enable-command-block']);
export const GameSettingResultSchema = z
  .object({
    key: RuleSettingKeySchema,
    rule: z.string(),
    dimension: z.string(),
    value: z.boolean(),
    status: z.enum(['applied', 'pending']),
  })
  .strict();
export type GameSettingResult = z.infer<typeof GameSettingResultSchema>;
export const GameSettingsRoutingSchema = z
  .object({
    rules: z.partialRecord(RuleSettingKeySchema, z.string()),
    unsupported: z.array(z.enum(['spawn-animals', 'spawn-npcs'])),
    paperNether: z.boolean(),
  })
  .strict();
export type GameSettingsRouting = z.infer<typeof GameSettingsRoutingSchema>;
export const legacySettingsRouting: GameSettingsRouting = { rules: {}, unsupported: [], paperNether: false };
export function gameSettingsRouting(version: string, family: string, versions: VersionCatalog): GameSettingsRouting {
  const migrated = versions.compare(version, '1.21.9') >= 0;
  const modern = versions.compare(version, '1.21.11') >= 0;
  return {
    rules: migrated
      ? Object.fromEntries(
          Object.entries(gameSettingRules).map(([key, rule]) => [key, modern ? rule.modern : rule.old]),
        )
      : {},
    unsupported: versions.compare(version, '1.21.2') >= 0 ? ['spawn-animals', 'spawn-npcs'] : [],
    paperNether: (family === 'paper' || family === 'purpur') && versions.compare(version, '26.1') >= 0,
  };
}
